import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import ProductDetailModal from "src/components/modals/ProductDetailModal";
import AddProductModal from "src/components/modals/AddProductModal";
import UploadProductsModal from "src/components/modals/UploadProductsModal";
import EditProductModal from "src/components/modals/EditProductModal";
import UpdateInventoryModal from "src/components/modals/UpdateInventoryModal";
import { cn, getCustomizationValue, renderCurrency, renderNumber, showSuccessToast } from "src/lib/utils";
import CustomDataTable from "src/components/CustomDataTable";
import CustomDropdown, { dropdownActionVariants } from "src/components/CustomDropdown";
import { EllipsisIcon } from "src/svg";
import { Button, buttonVariants } from "src/components/ui/button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { archiveInventory, getInventory } from "src/api/inventory";
import CustomSheet from "src/components/CustomSheet";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import CustomModal from "src/components/CustomModal";
import SearchInput from "src/components/widgets/SearchInput";
import { useDashboardContext } from "src/context/DashboardContext";
import CustomSelect from "src/components/CustomSelect";
import { useAuth } from "src/providers/AuthProvider";

const Inventory = () => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const { entities } = useDashboardContext();
	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get("search");
	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;
	const entity = searchParams.get("entity") ?? "";

	const updateSearchParams = (key, value) => {
		const newSearchParams = new URLSearchParams(searchParams);

		if (value === "0") {
			newSearchParams.delete(key);
		} else {
			newSearchParams.set(key, value);
		}

		setSearchParams(newSearchParams);
	};

	const { data, isLoading } = useQuery({
		queryKey: ["inventory", page, limit, search, entity],
		queryFn: () => getInventory({ page, limit, search, entity }),
	});

	const { mutate: archiveProduct } = useMutation(archiveInventory, {
		onSuccess: () => {
			showSuccessToast("Product archived successfully");
			queryClient.invalidateQueries(["inventory"]);
		},
	});

	const openRequestModal = () => {};

	const columns = [
		{ name: "Item Code", selector: (row) => row.item_id },
		{
			name: "Product Name",
			cell: (row) => (
				<div className="tw-gap-1 tw-flex tw-items-center">
					<img className="tw-w-10 tw-aspect-square tw-rounded-md tw-object-cover" src={row?.image} alt="" /> <span>{row.name}</span>
				</div>
			),
			grow: 1.75,
		},
		{ name: "Qty", selector: (row) => renderNumber(row.available_qty), grow: 0.55 },
		{ name: "SKU", selector: (row) => row.sku, grow: 0.55 },
		{ name: "Cost Price", selector: (row) => renderCurrency(row.cost_price) },
		{ name: "Selling Price", selector: (row) => renderCurrency(row.price) },
		{ name: "Added By", selector: (row) => row?.first_name?.concat(" ", row?.last_name) ?? "N/A" },
		{
			name: "Action",
			cell: (row) => (
				<CustomDropdown
					trigger={
						<Button variant="hover">
							<EllipsisIcon />
						</Button>
					}
					actions={[
						{
							label: (
								<CustomSheet title="Product Detail" trigger={<span>Detail</span>}>
									<ProductDetailModal product={row} />
								</CustomSheet>
							),
							asChild: true,
						},
						{
							label: (
								<CustomSheet title="Edit Product" trigger={<span>Edit</span>}>
									<EditProductModal product={row} />
								</CustomSheet>
							),
							asChild: true,
						},
						{
							label: (
								<CustomConfirmDialog
									trigger={<span className={dropdownActionVariants({ variant: "destructive" })}>Archive Product</span>}
									mutateFn={() => archiveProduct({ uuid: row.uuid })}
								/>
							),
							asChild: true,
						},
					]}
				/>
			),
			grow: 0.5,
		},
	];

	const inventory = data?.data?.data ?? [];
	const totalRow = data?.data?.total;

	return (
		<div className="pageWrapper">
			<div className="mainContent">
				<div className="pageTop flex-cont flex-space-between flex-align-center">
					<h2>{getCustomizationValue(user, "inventory_label", true)}</h2>

					<div className="pagetop_right flex-cont flex-end flex-align-center">
						<CustomSelect
							placeholder="Select Store/Warehouse"
							containerClassName="!tw-w-[230px]"
							value={entity}
							onChange={(value) => updateSearchParams("entity", value)}
							options={[{ value: "0", label: "All" }, ...entities?.map(({ name, uuid }) => ({ value: uuid, label: name }))]}
						/>
					</div>
				</div>

				<div className="innerContent">
					<ActionBar openRequestModal={openRequestModal} />

					<CustomDataTable columns={columns} data={inventory} progressPending={isLoading} paginationTotalRows={totalRow} />
				</div>
			</div>
		</div>
	);
};

// Reusable components
const ActionBar = () => {
	const { user } = useAuth();

	return (
		<div className="inner-top tw-gap-4">
			<div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap tw-ml-auto">
				<SearchInput name="search" placeholder="Search..." />
				<CustomDropdown
					trigger={<button className="btn green-btn dropdown-btn">Add New Product</button>}
					actions={[
						{
							label: (
								<CustomModal title="Add Single Product" trigger={<Button className="!tw-py-2.5">Add Single Product</Button>}>
									<AddProductModal />
								</CustomModal>
							),
							asChild: true,
						},
						{
							label: (
								<CustomModal title="Upload Bulk" trigger={<Button className="!tw-py-2.5">Upload Bulk</Button>}>
									<UploadProductsModal />
								</CustomModal>
							),
							asChild: true,
						},
					]}
				/>

				<CustomModal
					title={cn("Update ", getCustomizationValue(user, "inventory_label", true))}
					trigger={<button className="blue-btn">Update {getCustomizationValue(user, "inventory_label", true)}</button>}
				>
					<UpdateInventoryModal />
				</CustomModal>

				<Link to="/inventory/archived" className={cn("orange-btn", buttonVariants({ className: "!tw-h-[45px]" }))}>
					Archived {getCustomizationValue(user, "inventory_label", true)}
				</Link>
			</div>
		</div>
	);
};

export default Inventory;
